// Permissões do usuário logado.
//
// Caminho rápido: o Auth Hook (migration 003) injeta `permissions`, `user_role`
// e `active` no JWT, então saber se alguém pode fazer algo é ler um array em
// memória.
//
// Caminho lento: enquanto o Hook não for ativado no Dashboard, os claims não
// existem e a gente busca em profiles + role_permissions. Funciona igual, só
// custa uma consulta no login. É deliberado que nada quebre se o passo manual
// for esquecido — o sistema fica devagar, não fora do ar.
//
// Em qualquer dúvida, NEGA. Permissão é fail-closed: se o carregamento falhou,
// se o token não tem o claim, se o profile não foi aprovado — a resposta é não.

import { getSupabase } from './supabase';

export interface SessaoUsuario {
  userId: string;
  email: string | null;
  nome: string | null;
  papel: string | null;
  ativo: boolean;
  permissoes: ReadonlySet<string>;
}

let sessao: SessaoUsuario | null = null;

interface ClaimsCustomizados {
  active?: boolean;
  user_role?: string;
  permissions?: string[];
}

/** Decodifica o payload do JWT. Não valida assinatura — quem valida é o servidor. */
function lerClaims(accessToken: string): ClaimsCustomizados | null {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json))) as ClaimsCustomizados;
  } catch {
    return null;
  }
}

async function buscarNoBanco(userId: string): Promise<{ papel: string | null; ativo: boolean; permissoes: string[] }> {
  const supabase = getSupabase();

  const { data: perfil, error: erroPerfil } = await supabase
    .from('profiles')
    .select('active, role_id, roles(name)')
    .eq('id', userId)
    .maybeSingle();

  if (erroPerfil || !perfil) return { papel: null, ativo: false, permissoes: [] };

  const linha = perfil as unknown as {
    active: boolean;
    role_id: number | null;
    roles: { name: string } | { name: string }[] | null;
  };
  const roles = Array.isArray(linha.roles) ? linha.roles[0] : linha.roles;

  if (!linha.active || linha.role_id === null) {
    return { papel: roles?.name ?? null, ativo: false, permissoes: [] };
  }

  const { data: perms } = await supabase
    .from('role_permissions')
    .select('permission_key')
    .eq('role_id', linha.role_id);

  const chaves = (perms ?? []) as { permission_key: string }[];

  return {
    papel: roles?.name ?? null,
    ativo: true,
    permissoes: chaves.map((p) => p.permission_key),
  };
}

/** Carrega a sessão. Chamado uma vez pelo shell, depois do login. */
export async function carregarSessao(): Promise<SessaoUsuario | null> {
  const supabase = getSupabase();
  const { data } = await supabase.auth.getSession();
  const s = data.session;
  if (!s?.user) {
    sessao = null;
    return null;
  }

  const claims = lerClaims(s.access_token);
  const temClaimsDoHook = Array.isArray(claims?.permissions);

  let papel: string | null;
  let ativo: boolean;
  let permissoes: string[];

  if (temClaimsDoHook) {
    papel = claims?.user_role ?? null;
    ativo = claims?.active === true;
    permissoes = ativo ? (claims?.permissions ?? []) : [];
  } else {
    const doBanco = await buscarNoBanco(s.user.id);
    papel = doBanco.papel;
    ativo = doBanco.ativo;
    permissoes = doBanco.permissoes;
  }

  sessao = {
    userId: s.user.id,
    email: s.user.email ?? null,
    nome: (s.user.user_metadata?.['full_name'] as string | undefined) ?? null,
    papel,
    ativo,
    permissoes: new Set(permissoes),
  };
  return sessao;
}

export function getSessao(): SessaoUsuario | null {
  return sessao;
}

export function limparSessao(): void {
  sessao = null;
}

/** Fail-closed: sem sessão, sem aprovação ou sem a chave, a resposta é não. */
export function pode(chave: string): boolean {
  if (!sessao || !sessao.ativo) return false;
  return sessao.permissoes.has(chave);
}

/** Verdadeiro se o usuário tiver PELO MENOS UMA das chaves. */
export function podeAlguma(...chaves: string[]): boolean {
  return chaves.some((c) => pode(c));
}
